<template>
  <v-sheet class="mx-auto" max-width="2000">
    <v-card class="mx-auto" color="primary" variant="plain" elevation="5"
            title="Dispensations by calendar year"
            subtitle="Number of patients receiving dispensations is displayed on each bar. The last few years are estimated using linear regression.">
      <v-row no-gutters>
        <v-col>
          <GChart type="ColumnChart" :data="dispensationsByCalendarYearChartData"
                  :options="dispensationsByCalendarYearChartOptions"
                  v-if="dispensationsByCalendarYearChartData.length !== 0"/>
          <v-chip variant="elevated" size="x-large" density="default" v-else>
            No data to display. Please adjust your filters.
          </v-chip>
        </v-col>
      </v-row>
    </v-card>
    <v-card class="mx-auto" color="primary" variant="plain" elevation="5"
            title="Drug type used during follow-up time">
      <v-row no-gutters>
        <v-col>
          <GChart type="BubbleChart" :data="drugTypeUsedDuringFollowUpTimeMonthsFromDiagnosisChartData"
                  :options="drugTypeUsedDuringFollowUpTimeMonthsFromDiagnosisChartOptions"
                  v-if="drugTypeUsedDuringFollowUpTimeMonthsFromDiagnosisChartData.length !== 0"/>
          <v-chip variant="elevated" size="x-large" density="default" v-else>
            No data to display. Please adjust your filters.
          </v-chip>
          <GChart type="BubbleChart" :data="drugTypeUsedDuringFollowUpTimeDispensationDateChartData"
                  :options="drugTypeUsedDuringFollowUpTimeDispensationDateChartOptions"
                  v-if="drugTypeUsedDuringFollowUpTimeDispensationDateChartData.length !== 0"/>
          <v-chip variant="elevated" size="x-large" density="default" v-else>
            No data to display. Please adjust your filters.
          </v-chip>
          <!--
          <v-card class="mx-auto" color="primary" variant="plain" elevation="5"
                  title="Drug type used during follow-up time">
            <GChart type="ColumnChart" :data="patientsPerAtcCodeChartData"
                    :options="patientsPerAtcCodeChartOptions"
                    v-if="patientsPerAtcCodeChartData.length !== 0"/>
            <v-chip variant="elevated" size="x-large" density="default" v-else>
              No data to display. Please adjust your filters.
            </v-chip>
            <GChart type="BubbleChart" :data="drugTypeUsedDuringFollowUpTimeChartData"
                    :options="drugTypeUsedDuringFollowUpTimeChartOptions"
                    v-if="drugTypeUsedDuringFollowUpTimeChartData.length !== 0"/>
            <v-chip variant="elevated" size="x-large" density="default" v-else>
              No data to display. Please adjust your filters.
            </v-chip>
          </v-card>
          -->
        </v-col>
      </v-row>
    </v-card>
    <v-card class="mx-auto" color="primary" variant="plain" elevation="5"
            title="Top 1000 patients by dispensations">
      <v-row align="start" no-gutters>
        <v-col cols="10">
          <GChart :settings="{ packages :['timeline'] }" type="Timeline"
                  :data="topPatientDispensationsChartData"
                  :options="topPatientDispensationsChartOptions"
                  @ready="topPatientDispensationsChartReady"
                  v-if="topPatientDispensationsChartData.length !== 0"/>
          <v-chip variant="elevated" size="x-large" density="default" v-else>
            No data to display. Please adjust your filters.
          </v-chip>
        </v-col>
        <v-col scroll-target="#scrolling">
          <div class="number-box-left">
            <transition name="fade-quick" mode="out-in">
              <v-sheet max-height="400" id="scrolling" class="overflow-y-auto">
                <div :key="topPatientDispensationsData" v-html="topPatientDispensationsData">
                </div>
              </v-sheet>
            </transition>
          </div>
        </v-col>
      </v-row>
    </v-card>
  </v-sheet>
</template>

<script>
import {defineComponent} from 'vue';
import {GChart} from 'vue-google-charts'
import axios from "axios";

export default defineComponent({
  name: 'CancerTreatmentPatternPrescribedDrugs',
  props: {
    icd: String,
    ageAtDiagnosis: Array,
    diagnosisDate: Array,
    survivalTimeInYears: Array,
    metastasisStatusM: Array,
    metastasisStatusN: Array,
    atcCodes: Array,
    visible: Boolean
  },
  components: {
    GChart
  },
  data: () => ({
    topPatientDispensationsChartData: [],
    topPatientDispensationsChartOptions: {
      backgroundColor: {
        fill: '#364c5b'
      },
      title: 'Top 1000 patients by dispensations',
      titlePosition: 'out',
      titleTextStyle: {
        color: '#ffffff',
        bold: false
      },
      height: 800,
      timeline: {
        rowLabelStyle: {
          color: '#ffffff',
          italic: false
        },
        barLabelStyle: {
          color: '#ffffff',
          italic: false
        },
        showBarLabels: true
      },
      colors: ['#a6cee3', '#b2df8a', '#fb9a99', '#fdbf6f', '#cab2d6', '#ffff99', '#1f78b4', '#33a02c'],
      tooltip: {
        trigger: 'none'
      },
      enableInteractivity: true,
      avoidOverlappingGridLines: true
    },
    topPatientDispensationsData: '',
    dispensationsByCalendarYearChartData: [],
    dispensationsByCalendarYearChartOptions: {
      backgroundColor: {
        fill: '#364c5b'
      },
      height: 800,
      colors: ['#a6cee3', '#b2df8a', '#fb9a99', '#fdbf6f', '#cab2d6', '#ffff99', '#1f78b4', '#33a02c'],
      legend: {
        position: 'bottom',
        alignment: 'end',
        textStyle: {color: '#c0c0c0', fontSize: 16},
        scrollArrows: {
          activeColor: '#f0f0f0',
          inactiveColor: 'c0c0c0'
        },
        pagingTextStyle: {
          color: '#f0f0f0'
        }
      },
      series: {
        0: {
          targetAxisIndex: 0
        },
        1: {
          targetAxisIndex: 1,
          type: 'line'
        }
      },
      hAxis: {
        title: 'Year of dispensation',
        titleTextStyle: {
          color: '#f0f0f0',
          italic: false
        },
        gridlines: {
          count: 0
        },
        minorGridlines: {
          count: 0
        },
        textStyle: {
          color: '#ffffff'
        }
      },
      vAxes: {
        0: {
          title: 'Number of dispensations',
          titleTextStyle: {
            color: '#f0f0f0',
            italic: false
          },
          gridlines: {
            count: 0
          },
          minorGridlines: {
            count: 0
          },
          textStyle: {
            color: '#ffffff'
          }
        },
        1: {
          title: 'Average dispensations per patient',
          titleTextStyle: {
            color: '#f0f0f0',
            italic: false
          },
          gridlines: {
            count: 0
          },
          minorGridlines: {
            count: 0
          },
          textStyle: {
            color: '#ffffff'
          }
        }
      }
    },
    drugTypeUsedDuringFollowUpTimeMonthsFromDiagnosisChartData: [],
    drugTypeUsedDuringFollowUpTimeMonthsFromDiagnosisChartOptions: {},
    drugTypeUsedDuringFollowUpTimeDispensationDateChartData: [],
    drugTypeUsedDuringFollowUpTimeDispensationDateChartOptions: {},
    patientsPerAtcCodeChartData: [],
    patientsPerAtcCodeChartOptions: {
      backgroundColor: {
        fill: '#364c5b'
      },
      height: 800,
      colors: ['#a6cee3'],
      legend: 'none',
      hAxis: {
        title: 'ATC code',
        titleTextStyle: {
          color: '#f0f0f0',
          italic: false
        },
        ticks: [],
        textStyle: {
          color: '#ffffff'
        }
      },
      vAxis: {
        title: 'Number of patients',
        titleTextStyle: {
          color: '#f0f0f0',
          italic: false
        },
        ticks: [],
        textStyle: {
          color: '#ffffff'
        }
      }
    },
    drugTypeUsedDuringFollowUpTimeChartData: [],
    drugTypeUsedDuringFollowUpTimeChartOptions: {}
  }),
  methods: {
    async loadDrugTypeUsedDuringFollowUpTimeChartOptions() {
      let atcCodes = (await this.loadAtcCodes()).data;
      let dispensationYears = (await this.loadDispensationYears()).data;
      this.drugTypeUsedDuringFollowUpTimeMonthsFromDiagnosisChartOptions = {
        backgroundColor: {
          fill: '#364c5b'
        },
        height: 400,
        colors: ['#a6cee3', '#b2df8a', '#fb9a99', '#fdbf6f', '#cab2d6', '#ffff99', '#1f78b4', '#33a02c'],
        legend: 'none',
        hAxis: {
          title: 'Months from diagnosis',
          baselineColor: '#f0f0f0',
          titleTextStyle: {
            color: '#f0f0f0',
            italic: false
          },
          textStyle: {
            color: '#ffffff'
          },
          gridlines: {
            color: '#c0c0c0'
          },
          minorGridlines: {
            count: 0
          }
        },
        vAxis: {
          title: 'ATC code',
          baselineColor: '#f0f0f0',
          titleTextStyle: {
            color: '#f0f0f0',
            italic: false
          },
          ticks: atcCodes.map((atcCode, index) => {
            return {v: atcCodes.length - index, f: atcCode}
          }),
          textStyle: {
            color: '#ffffff'
          },
          gridlines: {
            color: '#364c5b'
          },
          minorGridlines: {
            count: 0
          },
          viewWindow: {
            min: 0,
            max: 7
          }
        },
        bubble: {
          opacity: 1.0,
          stroke: 'none'
        },
        sizeAxis: {
          minSize: 2,
          maxSize: 10
        },
        tooltip: {
          isHtml: true
        }
      };
      this.drugTypeUsedDuringFollowUpTimeDispensationDateChartOptions = {
        backgroundColor: {
          fill: '#364c5b'
        },
        height: 400,
        colors: ['#a6cee3', '#b2df8a', '#fb9a99', '#fdbf6f', '#cab2d6', '#ffff99', '#1f78b4', '#33a02c'],
        legend: 'none',
        hAxis: {
          title: 'Dispensation year',
          baselineColor: '#f0f0f0',
          titleTextStyle: {
            color: '#f0f0f0',
            italic: false
          },
          ticks: dispensationYears.map(dispensationYear => {
            return {v: dispensationYear * 100, f: dispensationYear.toString()}
          }),
          textStyle: {
            color: '#ffffff'
          },
          gridlines: {
            color: '#c0c0c0'
          },
          minorGridlines: {
            count: 0
          }
        },
        vAxis: {
          title: 'ATC code',
          baselineColor: '#f0f0f0',
          titleTextStyle: {
            color: '#f0f0f0',
            italic: false
          },
          ticks: atcCodes.map((atcCode, index) => {
            return {v: atcCodes.length - index, f: atcCode}
          }),
          textStyle: {
            color: '#ffffff'
          },
          gridlines: {
            color: '#364c5b'
          },
          minorGridlines: {
            count: 0
          },
          viewWindow: {
            min: 0,
            max: 7
          }
        },
        bubble: {
          opacity: 1.0,
          stroke: 'none'
        },
        sizeAxis: {
          minSize: 2,
          maxSize: 10
        },
        tooltip: {
          isHtml: true
        }
      };
      this.drugTypeUsedDuringFollowUpTimeChartOptions = {
        backgroundColor: {
          fill: '#364c5b'
        },
        title: 'Number of dispensations',
        titlePosition: 'out',
        titleTextStyle: {
          color: '#ffffff',
          bold: false
        },
        height: 800,
        colors: ['#a6cee3', '#b2df8a', '#fb9a99', '#fdbf6f', '#cab2d6', '#ffff99', '#1f78b4', '#33a02c'],
        legend: {
          position: 'right',
          alignment: 'end',
          textStyle: {color: '#c0c0c0', fontSize: 16}
        },
        hAxis: {
          title: 'Year of dispensation',
          titleTextStyle: {
            color: '#f0f0f0',
            italic: false
          },
          viewWindow: {
            min: Math.min(...dispensationYears) - 1,
            max: Math.max(...dispensationYears) + 1
          },
          textStyle: {
            color: '#ffffff'
          }
        },
        vAxis: {
          title: 'Year from diagnosis',
          titleTextStyle: {
            color: '#f0f0f0',
            italic: false
          },
          viewWindow: {
            min: -1,
            max: 200
          },
          textStyle: {
            color: '#ffffff'
          }
        }
      };
    },
    async loadTopPatientDispensationsData() {
      this.topPatientDispensationsData = '';
      return axios.get('cancer-top-patient-dispensations',
          {
            headers: { Authorization: `Bearer ${this.$store.getters.stateUser.keycloak_token}` },
            params: {
              icd: this.icd,
              age_at_diagnosis: this.ageAtDiagnosis.join(","),
              year_of_diagnosis: this.diagnosisDate.join(","),
              survival_time_in_years: this.survivalTimeInYears.join(","),
              metastasis_status_m: this.metastasisStatusM.join(","),
              metastasis_status_n: this.metastasisStatusN.join(","),
              atc_codes: this.atcCodes.join(",")
            }
          });
    },
    async topPatientDispensationsChartReady(chart, google) {
      const setTopPatientDispensationsData = this.setTopPatientDispensationsData;
      google.visualization.events.addListener(chart, 'select', function () {
        const selectedItem = chart.getSelection()[0];
        if (selectedItem) {
          setTopPatientDispensationsData(selectedItem.row + 1);
        }
      })
    },
    async setTopPatientDispensationsData(selectedItem) {
      this.topPatientDispensationsData = this.topPatientDispensationsChartData[selectedItem][2];
    },
    async loadAtcCodes() {
      return axios.get('/cancer-atc-codes',
          {
            headers: { Authorization: `Bearer ${this.$store.getters.stateUser.keycloak_token}` },
            params: {
              icd: this.icd
            }
          });
    },
    async loadDispensationYears() {
      return axios.get('/cancer-dispensation-years',
          {
            headers: { Authorization: `Bearer ${this.$store.getters.stateUser.keycloak_token}` },
            params: {
              icd: this.icd
            }
          });
    },
    async loadDispensationsByCalendarYearChartData() {
      return axios.get('cancer-dispensations-by-calendar-year-data',
          {
            headers: { Authorization: `Bearer ${this.$store.getters.stateUser.keycloak_token}` },
            params: {
              icd: this.icd,
              age_at_diagnosis: this.ageAtDiagnosis.join(","),
              year_of_diagnosis: this.diagnosisDate.join(","),
              survival_time_in_years: this.survivalTimeInYears.join(","),
              metastasis_status_m: this.metastasisStatusM.join(","),
              metastasis_status_n: this.metastasisStatusN.join(","),
              atc_codes: this.atcCodes.join(",")
            }
          });
    },
    async loadDrugTypeUsedDuringFollowUpTimeMonthsFromDiagnosisChartData() {
      return axios.get('cancer-drug-type-used-during-follow-up-time-months-from-diagnosis-data',
          {
            headers: { Authorization: `Bearer ${this.$store.getters.stateUser.keycloak_token}` },
            params: {
              icd: this.icd,
              age_at_diagnosis: this.ageAtDiagnosis.join(","),
              year_of_diagnosis: this.diagnosisDate.join(","),
              survival_time_in_years: this.survivalTimeInYears.join(","),
              metastasis_status_m: this.metastasisStatusM.join(","),
              metastasis_status_n: this.metastasisStatusN.join(","),
              atc_codes: this.atcCodes.join(",")
            }
          });
    },
    async loadDrugTypeUsedDuringFollowUpTimeDispensationDateChartData() {
      return axios.get('cancer-drug-type-used-during-follow-up-time-dispensation-date-data',
          {
            headers: { Authorization: `Bearer ${this.$store.getters.stateUser.keycloak_token}` },
            params: {
              icd: this.icd,
              age_at_diagnosis: this.ageAtDiagnosis.join(","),
              year_of_diagnosis: this.diagnosisDate.join(","),
              survival_time_in_years: this.survivalTimeInYears.join(","),
              metastasis_status_m: this.metastasisStatusM.join(","),
              metastasis_status_n: this.metastasisStatusN.join(","),
              atc_codes: this.atcCodes.join(",")
            }
          });
    },
    async loadPatientsPerAtcCodeChartData() {
      let data = axios.get('cancer-patients-per-atc-code-data',
          {
            headers: { Authorization: `Bearer ${this.$store.getters.stateUser.keycloak_token}` },
            params: {
              icd: this.icd,
              age_at_diagnosis: this.ageAtDiagnosis.join(","),
              year_of_diagnosis: this.diagnosisDate.join(","),
              survival_time_in_years: this.survivalTimeInYears.join(","),
              metastasis_status_m: this.metastasisStatusM.join(","),
              metastasis_status_n: this.metastasisStatusN.join(","),
              atc_codes: this.atcCodes.join(",")
            }
          });
      data.then((response) => {
        this.patientsPerAtcCodeChartData = response.data;
      });
    },
    async loadDrugTypeUsedDuringFollowUpTimeChartData() {
      let data = axios.get('cancer-drug-type-used-during-follow-up-time-data',
          {
            headers: { Authorization: `Bearer ${this.$store.getters.stateUser.keycloak_token}` },
            params: {
              icd: this.icd,
              age_at_diagnosis: this.ageAtDiagnosis.join(","),
              year_of_diagnosis: this.diagnosisDate.join(","),
              survival_time_in_years: this.survivalTimeInYears.join(","),
              metastasis_status_m: this.metastasisStatusM.join(","),
              metastasis_status_n: this.metastasisStatusN.join(","),
              atc_codes: this.atcCodes.join(",")
            }
          });
      data.then((response) => {
        this.drugTypeUsedDuringFollowUpTimeChartData = response.data;
      });
    },
    async loadDynamicData() {
      await Promise.all([
        this.loadDispensationsByCalendarYearChartData(),
        this.loadDrugTypeUsedDuringFollowUpTimeMonthsFromDiagnosisChartData(),
        this.loadDrugTypeUsedDuringFollowUpTimeDispensationDateChartData(),
        this.loadTopPatientDispensationsData()
        /*this.loadPatientsPerAtcCodeChartData(),
        this.loadDrugTypeUsedDuringFollowUpTimeChartData()*/
      ]).then(responses => {
        this.dispensationsByCalendarYearChartData = responses[0].data;
        this.drugTypeUsedDuringFollowUpTimeMonthsFromDiagnosisChartData = responses[1].data;
        this.drugTypeUsedDuringFollowUpTimeDispensationDateChartData = responses[2].data;
        this.topPatientDispensationsChartData = responses[3].data;
        console.log('Dynamic data loaded CancerTreatmentPatternPrescribedDrugs')
      })
    }
  },
  mounted() {
    this.loadDrugTypeUsedDuringFollowUpTimeChartOptions().then(() => {
      this.loadDynamicData()
    })
  },
  updated() {
    if (this.visible) {
      this.loadDynamicData()
    }
  }
});
</script>
